import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, Eye, EyeOff, Save, User, ArrowLeft } from 'lucide-react';
import { API_URL } from '../config/api.ts';
import BackgroundPreviewModal from './BackgroundPreviewModal.tsx';

interface DeviceSettings {
  positionX: number;
  positionY: number;
  zoom: number;
}

interface BackgroundSettings {
  desktop: DeviceSettings;
  mobile: DeviceSettings;
}

interface Character {
  _id: string;
  name: string;
  role?: string;
  description: string;
  greeting: string;
  personality: string;
  scenario: string;
  privateStory?: string;
  avatar?: string;
  backgroundImage?: string;
  backgroundSettings?: BackgroundSettings;
  isPublic: boolean;
  isNSFW: boolean;
  hidePersonality: boolean;
  hideCreator?: boolean;
  isDefault: boolean;
  category: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

interface CharacterManagerProps {
  isOpen: boolean;
  onClose: () => void;
  startInCreateMode?: boolean;
  marginLeft?: string;
}

const CharacterManager: React.FC<CharacterManagerProps> = ({
  isOpen,
  onClose,
  startInCreateMode = false,
  marginLeft = '0'
}) => {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<Character | null>(null);
  const [isCreating, setIsCreating] = useState(startInCreateMode);
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const totalSteps = 4;

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    role: '',              // NEW: Role/Title
    publicStory: '',       // Was: description
    privateStory: '',      // NEW: Goes to systemPrompt
    scenarioIntro: '',     // Was: greeting
    personality: '',
    category: 'general',
    tags: '',
    isPublic: true,        // Default to public
    isNSFW: false,
    hidePersonality: false, // Allow hiding personality from public view
    hideCreator: false      // Allow hiding creator name
  });

  // Image upload states
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [backgroundPreview, setBackgroundPreview] = useState<string>('');

  // Background settings state
  const [backgroundSettings, setBackgroundSettings] = useState<BackgroundSettings>({
    desktop: { positionX: 50, positionY: 50, zoom: 100 },
    mobile: { positionX: 50, positionY: 50, zoom: 100 }
  });
  const [showBackgroundPreviewModal, setShowBackgroundPreviewModal] = useState(false);

  // Sync isCreating with startInCreateMode prop when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsCreating(startInCreateMode);
      setCurrentStep(1);
      fetchUserCharacters();
    }
  }, [isOpen, startInCreateMode]);

  if (!isOpen) return null;

  const fetchUserCharacters = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/characters`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCharacters(data.characters || []);
      }
    } catch (error) {
      console.error('Error fetching characters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Character name is required');
      return;
    }

    setLoading(true);

    try {
      const tags = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);

      // Preparar FormData para archivos
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('role', formData.role);
      formDataToSend.append('publicStory', formData.publicStory);
      formDataToSend.append('privateStory', formData.privateStory);
      formDataToSend.append('scenarioIntro', formData.scenarioIntro);
      formDataToSend.append('personality', formData.personality);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('tags', JSON.stringify(tags));
      formDataToSend.append('isPublic', formData.isPublic.toString());
      formDataToSend.append('isNSFW', formData.isNSFW.toString());
      formDataToSend.append('hidePersonality', formData.hidePersonality.toString());
      if (formData.hideCreator !== undefined) {
        formDataToSend.append('hideCreator', formData.hideCreator.toString());
      }
      formDataToSend.append('backgroundSettings', JSON.stringify(backgroundSettings));

      // Agregar archivos si existen
      if (avatarFile) {
        formDataToSend.append('avatar', avatarFile);
      }
      if (backgroundFile) {
        formDataToSend.append('background', backgroundFile);
      }

      const url = editingCharacter
        ? `${API_URL}/characters/${editingCharacter._id}`
        : `${API_URL}/characters/create`;

      const method = editingCharacter ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        body: formDataToSend,
        credentials: 'include'
      });

      if (response.ok) {
        await fetchUserCharacters();
        setShowSuccess(true);
        // Auto-close after 2.5 seconds, then reset form
        setTimeout(() => {
          setShowSuccess(false);
          setIsCreating(false);
          setEditingCharacter(null);
          // Reset form AFTER closing the view so user doesn't see the flash
          resetForm();
        }, 2500);
      } else {
        const error = await response.json();
        if (error.error === 'DUPLICATE_NAME') {
          alert('⚠️ ' + error.message);
        } else {
          alert(error.message || 'Error saving character');
        }
      }
    } catch (error) {
      console.error('Error saving character:', error);
      alert('Error saving character');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (characterId: string) => {
    console.log('handleDelete called with characterId:', characterId);

    if (!window.confirm('Are you sure you want to delete this character?')) {
      console.log('Delete cancelled by user');
      return;
    }

    console.log('User confirmed deletion, making request...');

    try {
      const url = `${API_URL}/characters/${characterId}`;
      console.log('DELETE request URL:', url);

      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include'
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Delete response:', responseData);
        setCharacters(prev => prev.filter(char => char._id !== characterId));
        alert('Character deleted successfully');
      } else {
        const error = await response.json();
        console.error('Delete error response:', error);
        alert(error.message || 'Error deleting character');
      }
    } catch (error) {
      console.error('Error deleting character:', error);
      alert('Error deleting character');
    }
  };

  const handleVisibilityToggle = async (character: Character) => {
    try {
      const response = await fetch(`${API_URL}/characters/${character._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...character, isPublic: !character.isPublic }),
        credentials: 'include'
      });

      if (response.ok) {
        await fetchUserCharacters();
      }
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      publicStory: '',
      privateStory: '',
      scenarioIntro: '',
      personality: '',
      category: 'general',
      tags: '',
      isPublic: true,
      isNSFW: false,
      hidePersonality: false,
      hideCreator: false
    });
    setAvatarFile(null);
    setBackgroundFile(null);
    setAvatarPreview('');
    setBackgroundPreview('');
    setCurrentStep(1);
  };

  const handleImageChange = (file: File | null, type: 'avatar' | 'background') => {
    if (file) {
      // Validate file size immediately (max 10MB)
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > MAX_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        alert(`⚠️ El archivo es demasiado grande (${sizeMB}MB). El tamaño máximo permitido es 10MB.`);
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('⚠️ Tipo de archivo no válido. Solo se permiten: JPG, PNG, GIF, WebP');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'avatar') {
          setAvatarFile(file);
          setAvatarPreview(reader.result as string);
        } else {
          setBackgroundFile(file);
          setBackgroundPreview(reader.result as string);
          // Open preview modal for background images
          setShowBackgroundPreviewModal(true);
        }
      };
      reader.readAsDataURL(file);
    } else {
      if (type === 'avatar') {
        setAvatarFile(null);
        setAvatarPreview('');
      } else {
        setBackgroundFile(null);
        setBackgroundPreview('');
        // Reset settings when removing background
        setBackgroundSettings({
          desktop: { positionX: 50, positionY: 50, zoom: 100 },
          mobile: { positionX: 50, positionY: 50, zoom: 100 }
        });
      }
    }
  };

  const startEdit = (character: Character) => {
    setEditingCharacter(character);
    setFormData({
      name: character.name,
      role: character.role || '',
      publicStory: character.description,
      privateStory: character.privateStory || '',
      scenarioIntro: character.greeting,
      personality: character.personality,
      category: character.category,
      tags: character.tags.join(', '),
      isPublic: character.isPublic,
      isNSFW: character.isNSFW || false,
      hidePersonality: character.hidePersonality || false,
      hideCreator: character.hideCreator || false
    });
    // Load existing images as previews
    if (character.avatar && !character.avatar.includes('dicebear')) {
      setAvatarPreview(character.avatar);
    }
    if (character.backgroundImage && !character.backgroundImage.includes('default')) {
      setBackgroundPreview(character.backgroundImage);
    }
    // Load existing background settings
    if (character.backgroundSettings) {
      setBackgroundSettings(character.backgroundSettings);
    } else {
      setBackgroundSettings({
        desktop: { positionX: 50, positionY: 50, zoom: 100 },
        mobile: { positionX: 50, positionY: 50, zoom: 100 }
      });
    }
    setIsCreating(true);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(5, 5, 20, 0.8)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      marginLeft: marginLeft,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      <div style={{
        backgroundColor: 'rgba(31, 41, 55, 0.7)',
        backdropFilter: 'blur(16px)',
        borderRadius: '24px',
        width: '95%',
        maxWidth: '1100px',
        height: '90%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #374151',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #f0abfc 0%, #a855f7 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: 0
          }}>
            Character Forge
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#9ca3af',
              cursor: 'pointer',
              padding: '4px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'white'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
          >
            <X size={24} />
          </button>
        </div>

        {/* Success Animation */}
        {showSuccess ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                boxShadow: '0 20px 40px rgba(16, 185, 129, 0.4)'
              }}>
                <span style={{ fontSize: '48px' }}>✨</span>
              </div>
              <h2 style={{
                color: 'white',
                fontSize: '28px',
                fontWeight: '700',
                marginBottom: '12px',
                background: 'linear-gradient(135deg, #f0abfc 0%, #a855f7 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {editingCharacter ? 'Character Updated!' : 'Character Created!'}
              </h2>
              <p style={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '16px',
                marginBottom: '8px'
              }}>
                Your character is now ready to chat 💜
              </p>
              <p style={{
                color: 'rgba(255,255,255,0.4)',
                fontSize: '13px'
              }}>
                Closing automatically...
              </p>
            </div>
          </div>
        ) : (
          /* Content */
          <div style={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            minHeight: 0,
            position: 'relative'
          }}>
            {/* Lista de personajes */}
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                overflowY: 'auto',
                padding: '16px',
                flex: 1
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'white',
                    margin: 0
                  }}>
                    My Characters
                  </h3>
                  <button
                    onClick={() => {
                      resetForm();
                      setIsCreating(true);
                      setEditingCharacter(null);
                    }}
                    style={{
                      backgroundColor: '#2563eb',
                      color: 'white',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                  >
                    <Plus size={18} />
                    New
                  </button>
                </div>

                {loading && !characters.length ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: '#9ca3af'
                  }}>
                    Loading...
                  </div>
                ) : characters.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: '#9ca3af'
                  }}>
                    You haven't created any characters yet
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {characters.map(character => (
                      <div
                        key={character._id}
                        style={{
                          backgroundColor: '#374151',
                          borderRadius: '8px',
                          padding: '12px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px'
                        }}>
                          {/* Avatar */}
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            backgroundColor: '#4b5563',
                            overflow: 'hidden',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {character.avatar ? (
                              <img
                                src={character.avatar}
                                alt={character.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <User size={24} color="#9ca3af" />
                            )}
                          </div>

                          {/* Info */}
                          <div style={{ flex: 1 }}>
                            <h4 style={{
                              fontWeight: '600',
                              color: 'white',
                              margin: '0 0 4px 0',
                              fontSize: '16px'
                            }}>
                              {character.name}
                            </h4>
                            <p style={{
                              fontSize: '14px',
                              color: '#9ca3af',
                              margin: '0 0 8px 0',
                              lineHeight: '1.4',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {character.description}
                            </p>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <span style={{
                                fontSize: '12px',
                                backgroundColor: '#4b5563',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                color: 'white'
                              }}>
                                {character.category}
                              </span>
                              {character.isPublic ? (
                                <Eye size={14} color="#10b981" />
                              ) : (
                                <EyeOff size={14} color="#9ca3af" />
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              onClick={() => startEdit(character)}
                              style={{
                                padding: '4px',
                                background: 'none',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                color: '#60a5fa'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleVisibilityToggle(character)}
                              style={{
                                padding: '4px',
                                background: 'none',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                color: character.isPublic ? '#9ca3af' : '#10b981'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              {character.isPublic ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                            <button
                              onClick={() => handleDelete(character._id)}
                              style={{
                                padding: '4px',
                                background: 'none',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                color: '#ef4444'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

        {/* Formulario de creación/edición en Modal sobrepuesto */}
        {isCreating && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgb(15, 15, 30)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            animation: 'fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: '24px',
            overflow: 'hidden'
          }}>
            <style>
              {`
                        @keyframes fadeIn {
                            from { opacity: 0; transform: translateY(10px); }
                            to { opacity: 1; transform: translateY(0); }
                        }
                    `}
            </style>
            <div style={{
              padding: '16px',
              borderBottom: '1px solid #374151',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setEditingCharacter(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <ArrowLeft size={24} />
              </button>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: 'white',
                margin: 0,
                letterSpacing: '-0.02em'
              }}>
                {editingCharacter ? 'Refining Persona' : 'Forge New Character'}
              </h3>
            </div>

            {/* Progress Bar */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px 0',
              marginBottom: '20px'
            }}>
              {[1, 2, 3, 4].map((step) => (
                <div key={step} style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: currentStep >= step ? '#d946ef' : '#374151',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    border: currentStep === step ? '2px solid #f0abfc' : 'none',
                    transition: 'all 0.3s ease'
                  }}>
                    {step}
                  </div>
                  {step < 4 && (
                    <div style={{
                      width: '60px',
                      height: '2px',
                      backgroundColor: currentStep > step ? '#d946ef' : '#374151',
                      margin: '0 8px',
                      transition: 'all 0.3s ease'
                    }} />
                  )}
                </div>
              ))}
            </div>

            <div style={{
              overflowY: 'auto',
              flex: 1,
              maxWidth: '800px',
              margin: '0 auto',
              width: '100%',
              padding: '0 24px 24px 24px'
            }}>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>

                  {/* STEP 1: IDENTITY */}
                  {currentStep === 1 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', animation: 'fadeIn 0.3s' }}>
                      {/* Header with icon */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '16px',
                          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 8px 20px rgba(168, 85, 247, 0.3)'
                        }}>
                          <span style={{ fontSize: '24px' }}>✨</span>
                        </div>
                        <div>
                          <h4 style={{ color: 'white', margin: '0 0 4px 0', fontSize: '22px', fontWeight: '700' }}>Identity & Core</h4>
                          <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0, fontSize: '14px' }}>Define who your character is at their essence</p>
                        </div>
                      </div>

                      {/* Two column layout for Name and Category */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        {/* Name */}
                        <div style={{
                          background: 'linear-gradient(145deg, rgba(30, 27, 75, 0.8) 0%, rgba(46, 16, 101, 0.4) 100%)',
                          borderRadius: '16px',
                          padding: '20px',
                          border: '1px solid rgba(139, 92, 246, 0.15)'
                        }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f0abfc', marginBottom: '12px', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            <span style={{ fontSize: '16px' }}>👤</span> Name *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            maxLength={100}
                            style={{
                              width: '100%',
                              padding: '16px',
                              backgroundColor: 'rgba(15, 10, 40, 0.6)',
                              border: '2px solid transparent',
                              borderRadius: '12px',
                              color: 'white',
                              fontSize: '16px',
                              outline: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#a855f7';
                              e.currentTarget.style.boxShadow = '0 0 20px rgba(168, 85, 247, 0.2)';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = 'transparent';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                            placeholder="e.g., Lila"
                            autoFocus
                          />
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                            <span style={{ fontSize: '10px', color: formData.name.length >= 100 ? '#ef4444' : 'rgba(255,255,255,0.4)' }}>
                              {formData.name.length}/100
                            </span>
                          </div>
                        </div>

                        {/* Role/Title */}
                        <div style={{
                          background: 'linear-gradient(145deg, rgba(30, 27, 75, 0.8) 0%, rgba(46, 16, 101, 0.4) 100%)',
                          borderRadius: '16px',
                          padding: '20px',
                          border: '1px solid rgba(139, 92, 246, 0.15)'
                        }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f0abfc', marginBottom: '12px', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            <span style={{ fontSize: '16px' }}>🎭</span> Role / Title *
                          </label>
                          <input
                            type="text"
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            maxLength={200}
                            style={{
                              width: '100%',
                              padding: '16px',
                              backgroundColor: 'rgba(15, 10, 40, 0.6)',
                              border: '2px solid transparent',
                              borderRadius: '12px',
                              color: 'white',
                              fontSize: '16px',
                              outline: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#a855f7';
                              e.currentTarget.style.boxShadow = '0 0 20px rgba(168, 85, 247, 0.2)';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = 'transparent';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                            placeholder="e.g., Seductive Tease"
                          />
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                            <span style={{ fontSize: '10px', color: formData.role.length >= 200 ? '#ef4444' : 'rgba(255,255,255,0.4)' }}>
                              {formData.role.length}/200
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Category - moved to its own row */}
                      <div style={{
                        background: 'linear-gradient(145deg, rgba(30, 27, 75, 0.8) 0%, rgba(46, 16, 101, 0.4) 100%)',
                        borderRadius: '16px',
                        padding: '20px',
                        border: '1px solid rgba(139, 92, 246, 0.15)'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f0abfc', marginBottom: '12px', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <span style={{ fontSize: '16px' }}>🏷️</span> Category
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '16px',
                            backgroundColor: 'rgba(15, 10, 40, 0.6)',
                            border: '2px solid transparent',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '16px',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#a855f7';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = 'transparent';
                          }}
                        >
                          <option value="general">General</option>
                          <option value="anime">Anime</option>
                          <option value="games">Games</option>
                          <option value="movies">Movies</option>
                          <option value="books">Books</option>
                          <option value="original">Original</option>
                          <option value="fantasy">Fantasy</option>
                          <option value="scifi">Sci-Fi</option>
                        </select>
                      </div>

                      {/* Public Story - Full width */}
                      <div style={{
                        background: 'linear-gradient(145deg, rgba(30, 27, 75, 0.8) 0%, rgba(46, 16, 101, 0.4) 100%)',
                        borderRadius: '16px',
                        padding: '24px',
                        border: '1px solid rgba(139, 92, 246, 0.15)'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f0abfc', marginBottom: '12px', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <span style={{ fontSize: '16px' }}>📝</span> Public Story (Short Bio)
                        </label>
                        <textarea
                          value={formData.publicStory}
                          onChange={(e) => setFormData({ ...formData, publicStory: e.target.value })}
                          maxLength={2000}
                          style={{
                            width: '100%',
                            padding: '16px',
                            backgroundColor: 'rgba(15, 10, 40, 0.6)',
                            border: '2px solid transparent',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '15px',
                            minHeight: '140px',
                            resize: 'vertical',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            lineHeight: '1.7',
                            fontFamily: 'inherit'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#a855f7';
                            e.currentTarget.style.boxShadow = '0 0 20px rgba(168, 85, 247, 0.2)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = 'transparent';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          placeholder="One-paragraph public bio seen on cards. Describe their appearance and vibe..."
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                          <span style={{ fontSize: '10px', color: formData.publicStory.length >= 2000 ? '#ef4444' : 'rgba(255,255,255,0.4)' }}>
                            {formData.publicStory.length}/2000
                          </span>
                        </div>
                        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', margin: '12px 0 0 0' }}>
                          This is the public profile shown to other users.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* STEP 2: PERSONALITY & STORY */}
                  {currentStep === 2 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', animation: 'fadeIn 0.3s' }}>
                      {/* Header with icon */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '16px',
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 8px 20px rgba(139, 92, 246, 0.3)'
                        }}>
                          <span style={{ fontSize: '24px' }}>🎭</span>
                        </div>
                        <div>
                          <h4 style={{ color: 'white', margin: '0 0 4px 0', fontSize: '22px', fontWeight: '700' }}>Personality & Story</h4>
                          <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0, fontSize: '14px' }}>Define how your character thinks and behaves</p>
                        </div>
                      </div>

                      {/* Private Story / Prompt */}
                      <div style={{
                        background: 'linear-gradient(145deg, rgba(30, 27, 75, 0.8) 0%, rgba(46, 16, 101, 0.4) 100%)',
                        borderRadius: '16px',
                        padding: '24px',
                        border: '1px solid rgba(139, 92, 246, 0.15)'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f0abfc', marginBottom: '12px', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <span style={{ fontSize: '16px' }}>🔒</span> Private Story / Prompt (for the model)
                        </label>
                        <textarea
                          value={formData.privateStory}
                          onChange={(e) => setFormData({ ...formData, privateStory: e.target.value })}
                          maxLength={5000}
                          style={{
                            width: '100%',
                            padding: '16px',
                            backgroundColor: 'rgba(15, 10, 40, 0.6)',
                            border: '2px solid transparent',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '15px',
                            minHeight: '120px',
                            resize: 'vertical',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            lineHeight: '1.7',
                            fontFamily: 'inherit'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#a855f7';
                            e.currentTarget.style.boxShadow = '0 0 20px rgba(168, 85, 247, 0.2)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = 'transparent';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          placeholder="Deeper backstory/constraints for the AI. This won't be visible to users..."
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                          <span style={{ fontSize: '10px', color: formData.privateStory.length >= 5000 ? '#ef4444' : 'rgba(255,255,255,0.4)' }}>
                            {formData.privateStory.length}/5000
                          </span>
                        </div>
                      </div>

                      {/* Scenario Intro */}
                      <div style={{
                        background: 'linear-gradient(145deg, rgba(30, 27, 75, 0.8) 0%, rgba(46, 16, 101, 0.4) 100%)',
                        borderRadius: '16px',
                        padding: '24px',
                        border: '1px solid rgba(139, 92, 246, 0.15)'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f0abfc', marginBottom: '12px', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <span style={{ fontSize: '16px' }}>🎬</span> Scenario Intro (cinematic first scene)
                        </label>
                        <textarea
                          value={formData.scenarioIntro}
                          onChange={(e) => setFormData({ ...formData, scenarioIntro: e.target.value })}
                          maxLength={5000}
                          style={{
                            width: '100%',
                            padding: '16px',
                            backgroundColor: 'rgba(15, 10, 40, 0.6)',
                            border: '2px solid transparent',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '15px',
                            minHeight: '100px',
                            resize: 'vertical',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            lineHeight: '1.7',
                            fontFamily: 'inherit'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#a855f7';
                            e.currentTarget.style.boxShadow = '0 0 20px rgba(168, 85, 247, 0.2)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = 'transparent';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          placeholder="Opening lines / stage directions. e.g. *Sighs looking at the horizon* Oh, I didn't see you there..."
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                          <span style={{ fontSize: '10px', color: formData.scenarioIntro.length >= 5000 ? '#ef4444' : 'rgba(255,255,255,0.4)' }}>
                            {formData.scenarioIntro.length}/5000
                          </span>
                        </div>
                      </div>

                      {/* Personality */}
                      <div style={{
                        background: 'linear-gradient(145deg, rgba(30, 27, 75, 0.8) 0%, rgba(46, 16, 101, 0.4) 100%)',
                        borderRadius: '16px',
                        padding: '24px',
                        border: '1px solid rgba(139, 92, 246, 0.15)'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f0abfc', marginBottom: '12px', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <span style={{ fontSize: '16px' }}>💭</span> Personality
                        </label>
                        <textarea
                          value={formData.personality}
                          onChange={(e) => setFormData({ ...formData, personality: e.target.value })}
                          maxLength={5000}
                          style={{
                            width: '100%',
                            padding: '16px',
                            backgroundColor: 'rgba(15, 10, 40, 0.6)',
                            border: '2px solid transparent',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '15px',
                            minHeight: '120px',
                            resize: 'vertical',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            lineHeight: '1.7',
                            fontFamily: 'inherit'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#a855f7';
                            e.currentTarget.style.boxShadow = '0 0 20px rgba(168, 85, 247, 0.2)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = 'transparent';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          placeholder="Tone, boundaries, likes/dislikes, control level, etc..."
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                          <span style={{ fontSize: '10px', color: formData.personality.length >= 5000 ? '#ef4444' : 'rgba(255,255,255,0.4)' }}>
                            {formData.personality.length}/5000
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 3: VISUALS */}
                  {currentStep === 3 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', animation: 'fadeIn 0.3s' }}>
                      <h4 style={{ color: '#e879f9', margin: '0 0 8px 0', fontSize: '18px' }}>Visuals</h4>

                      {/* Avatar */}
                      <div>
                        <label style={{ display: 'block', color: '#e9d5ff', marginBottom: '8px', fontWeight: '500' }}>Avatar Image</label>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                          <div style={{
                            width: '100px',
                            height: '100px',
                            borderRadius: '12px',
                            backgroundColor: '#1e1b4b',
                            border: '2px dashed #4c1d95',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {avatarPreview ? (
                              <img src={avatarPreview} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <User size={32} color="#4c1d95" />
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageChange(e.target.files?.[0] || null, 'avatar')}
                              style={{
                                width: '100%',
                                padding: '8px',
                                backgroundColor: '#1e1b4b',
                                borderRadius: '8px',
                                color: '#e9d5ff',
                                border: '1px solid #4c1d95'
                              }}
                            />
                            <p style={{ fontSize: '13px', color: '#a78bfa', marginTop: '8px' }}>Recommended: Square image, max 5MB.</p>
                          </div>
                        </div>
                      </div>

                      {/* Background */}
                      <div>
                        <label style={{ display: 'block', color: '#e9d5ff', marginBottom: '8px', fontWeight: '500' }}>Background Image</label>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                          <div style={{
                            width: '160px',
                            height: '90px',
                            borderRadius: '12px',
                            backgroundColor: '#1e1b4b',
                            border: '2px dashed #4c1d95',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {backgroundPreview ? (
                              <img src={backgroundPreview} alt="Background" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ color: '#4c1d95', fontSize: '12px' }}>16:9</span>
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageChange(e.target.files?.[0] || null, 'background')}
                              style={{
                                width: '100%',
                                padding: '8px',
                                backgroundColor: '#1e1b4b',
                                borderRadius: '8px',
                                color: '#e9d5ff',
                                border: '1px solid #4c1d95'
                              }}
                            />
                            <p style={{ fontSize: '13px', color: '#a78bfa', marginTop: '8px' }}>Recommended: 1920x1080, max 10MB.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 4: SETTINGS */}
                  {currentStep === 4 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', animation: 'fadeIn 0.3s' }}>
                      <h4 style={{ color: '#e879f9', margin: '0 0 8px 0', fontSize: '18px' }}>Final Touches</h4>

                      {/* Tags */}
                      <div>
                        <label style={{ display: 'block', color: '#e9d5ff', marginBottom: '8px', fontWeight: '500' }}>Tags</label>
                        <input
                          type="text"
                          value={formData.tags}
                          onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '12px',
                            backgroundColor: '#1e1b4b',
                            border: '1px solid #4c1d95',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '16px',
                            outline: 'none'
                          }}
                          placeholder="Comma separated tags (e.g. funny, heroic, anime)"
                        />
                      </div>

                      {/* Visibility Toggle */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#1e1b4b',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid #4c1d95'
                      }}>
                        <div>
                          <h5 style={{ color: 'white', margin: '0 0 4px 0', fontSize: '16px' }}>Public Character</h5>
                          <p style={{ color: '#a78bfa', margin: 0, fontSize: '13px' }}>Allow other users to chat with this character.</p>
                        </div>
                        <div
                          onClick={() => setFormData({ ...formData, isPublic: !formData.isPublic })}
                          style={{
                            width: '50px',
                            height: '28px',
                            backgroundColor: formData.isPublic ? '#d946ef' : '#4b5563',
                            borderRadius: '20px',
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: formData.isPublic ? '24px' : '2px',
                            transition: 'left 0.2s',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </div>

                      {/* NSFW Toggle */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#1e1b4b',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid #ef4444' // Red border for warning
                      }}>
                        <div>
                          <h5 style={{ color: '#fca5a5', margin: '0 0 4px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            NSFW Content
                            <span style={{ fontSize: '10px', background: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>18+</span>
                          </h5>
                          <p style={{ color: '#fca5a5', margin: 0, fontSize: '13px', opacity: 0.8 }}>Mark this character as Not Safe For Work.</p>
                        </div>
                        <div
                          onClick={() => setFormData({ ...formData, isNSFW: !formData.isNSFW })}
                          style={{
                            width: '50px',
                            height: '28px',
                            backgroundColor: formData.isNSFW ? '#ef4444' : '#4b5563',
                            borderRadius: '20px',
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: formData.isNSFW ? '24px' : '2px',
                            transition: 'left 0.2s',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </div>

                      {/* Hide Personality Toggle */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#1e1b4b',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid #6366f1'
                      }}>
                        <div>
                          <h5 style={{ color: '#a5b4fc', margin: '0 0 4px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            🔒 Hide Personality
                          </h5>
                          <p style={{ color: '#a78bfa', margin: 0, fontSize: '13px' }}>Hide your character's personality from other users.</p>
                        </div>
                        <div
                          onClick={() => setFormData({ ...formData, hidePersonality: !formData.hidePersonality })}
                          style={{
                            width: '50px',
                            height: '28px',
                            backgroundColor: formData.hidePersonality ? '#6366f1' : '#4b5563',
                            borderRadius: '20px',
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: formData.hidePersonality ? '24px' : '2px',
                            transition: 'left 0.2s',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </div>

                      {/* Hide Creator Toggle */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#1e1b4b',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid #14b8a6'
                      }}>
                        <div>
                          <h5 style={{ color: '#5eead4', margin: '0 0 4px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            👻 Hide Creator Name
                          </h5>
                          <p style={{ color: '#99f6e4', margin: 0, fontSize: '13px' }}>Show "Anonymous" instead of your username.</p>
                        </div>
                        <div
                          onClick={() => setFormData({ ...formData, hideCreator: !formData.hideCreator })}
                          style={{
                            width: '50px',
                            height: '28px',
                            backgroundColor: formData.hideCreator ? '#14b8a6' : '#4b5563',
                            borderRadius: '20px',
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: formData.hideCreator ? '24px' : '2px',
                            transition: 'left 0.2s',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </div>
                    </div>
                  )}

                </div>

                {/* Navigation Buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid #374151' }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (currentStep > 1) setCurrentStep(prev => prev - 1);
                      else {
                        setIsCreating(false);
                        setEditingCharacter(null);
                      }
                    }}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'transparent',
                      color: '#9ca3af',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'white'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                  >
                    {currentStep === 1 ? 'Cancel' : 'Back'}
                  </button>

                  <button
                    type={currentStep === totalSteps ? 'submit' : 'button'}
                    onClick={(e) => {
                      if (currentStep < totalSteps) {
                        e.preventDefault(); // Prevent submit
                        // Basic validation per step could go here
                        if (currentStep === 1 && !formData.name) {
                          alert('Please enter a name');
                          return;
                        }
                        setCurrentStep(prev => prev + 1);
                      }
                      // If it's the last step, let the form submit naturally
                    }}
                    disabled={loading}
                    style={{
                      padding: '12px 32px',
                      background: 'linear-gradient(135deg, #d946ef 0%, #8b5cf6 100%)',
                      color: 'white',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(217, 70, 239, 0.3)',
                      opacity: loading ? 0.7 : 1,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(217, 70, 239, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 70, 239, 0.3)';
                      }
                    }}
                    onMouseDown={(e) => !loading && (e.currentTarget.style.transform = 'translateY(0) scale(0.96)')}
                    onMouseUp={(e) => !loading && (e.currentTarget.style.transform = 'translateY(-2px) scale(1)')}
                  >
                    {loading ? 'Saving...' : (currentStep === totalSteps ? (editingCharacter ? 'Update Character' : 'Create Character') : 'Continue')}
                  </button>
                </div>

              </form>
            </div>
          </div>
        )}
      </div >

      {/* Background Preview Modal */}
      <BackgroundPreviewModal
        isOpen={showBackgroundPreviewModal}
        imageUrl={backgroundPreview}
        initialSettings={backgroundSettings}
        onApply={(settings) => {
          setBackgroundSettings(settings);
          setShowBackgroundPreviewModal(false);
        }}
        onClose={() => setShowBackgroundPreviewModal(false)}
      />
    </div >
  );
};

export default CharacterManager;


